import {
  getGoodsStockPercent,
  isGoodsLowStock,
  isGoodsSoldOut,
  type YearEndGoodsInstitution,
} from '@/api/getYearEndGoodsInstitutionList';

export const buildGoodsGiftCards = (institutions: YearEndGoodsInstitution[]) =>
  institutions.flatMap((institution) => {
    const { giftProduct } = institution;

    if (!giftProduct) {
      return [];
    }

    return [
      {
        institution,
        gift: {
          institutionId: institution.institutionId,
          institutionName: institution.institutionName,
          productNumber: giftProduct.productNumber,
          productName: giftProduct.productName,
          mainImageUrl: giftProduct.mainImageUrl,
          requiredDonationAmount: giftProduct.requiredDonationAmount,
          reviewRating: giftProduct.reviewRating,
          reviewCount: giftProduct.reviewCount,
        },
        readCount: giftProduct.readCount,
      },
    ];
  });

export type GoodsGiftCardItem = ReturnType<typeof buildGoodsGiftCards>[number];

//* 지역 로고 순서가 늘 같으면 앞쪽 지역만 노출되므로 요청마다 섞는다
export const shuffleInstitutions = (
  institutions: YearEndGoodsInstitution[],
  random: () => number = Math.random,
) => {
  const shuffled = [...institutions];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [
      shuffled[swapIndex],
      shuffled[index],
    ];
  }

  return shuffled;
};

export const shuffleGoodsProviderRegions = (
  institutions: YearEndGoodsInstitution[],
  random: () => number = Math.random,
) =>
  shuffleInstitutions(
    institutions.filter((institution) => !isGoodsSoldOut(institution)),
    random,
  );

export const getGoodsStockPill = (institution: YearEndGoodsInstitution) => {
  if (isGoodsSoldOut(institution)) {
    return { label: '한정 굿즈', value: '마감', tone: 'soldOut' } as const;
  }

  return {
    label: '한정 굿즈 재고',
    value: `${getGoodsStockPercent(institution)}%`,
    tone: isGoodsLowStock(institution) ? 'low' : 'normal',
  } as const;
};

//* 루프 캐러셀은 마지막 열이 첫 열 옆에 붙으므로, 홀수면 1장짜리 열이 중간 빈 칸이 된다 — 목록을 한 번 더 이어 짝수로 맞춘다
export const toLoopingCardColumns = <T>(cards: T[]) => {
  const evenCards =
    cards.length % 2 === 0 || cards.length === 1 ? cards : [...cards, ...cards];

  return evenCards.flatMap((card, index) =>
    index % 2 === 0 ? [evenCards.slice(index, index + 2)] : [],
  );
};
